import {createCompetitiveBuildingContent,
  createCompetitiveDataCenterContent,
  createCompetitiveMultiFamilyBuildingContent,
  createCompetitivePortfolioContent,
  createIndustrialBuildingContent,
  createProjectContent,
} from '@/lib/classes/InfoWindowContent';
import {formatCompsForExport} from '@/lib/classes/InfoWindowContent/helpers/competitive';
import {Link} from '@/lib/classes/Link';
import {DisplayField} from '../DisplayField';

/**
 * @typedef {import('@/lib/classes/InfoWindowContent').InfoWindowContent} InfoWindowContent
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').Comp} Comp
 * @typedef {import('@/lib/classes/InfoWindowContent/aggregators/shared.js').Aggregator} Aggregator
 */

/**
 * A map of export format functions for each type of marker
 * @return {Map<string, Function>}
 * @param {Object} marker - The marker object
 * @param {string} [id] - The optional id for the marker
 */
const exportFormatFunctions = new Map([
  ['industrialBuildings', (marker) => createIndustrialBuildingContent(marker, {includeMondayComps: false})],
  ['industrialProjects', (marker) => createProjectContent(marker, false)],
  ['competitiveBuildings', (marker) => createCompetitiveBuildingContent(marker)],
  ['competitiveBuildingComps', (marker, id) => formatCompsForExport(marker, id)],
  ['competitivePortfolios', (marker) => createCompetitivePortfolioContent(marker)],
  ['overlays', null],
  ['dataCenterBuildings', (marker) => createCompetitiveDataCenterContent(marker)],
  ['multiFamilyProjects', (marker) => createProjectContent(marker, false)],
  ['multiFamilyBuildings', (marker) => createCompetitiveMultiFamilyBuildingContent(marker)],
  ['acquisitionProjects', (marker) => createProjectContent(marker, false)],
  ['dataCenterProjects', (marker) => createProjectContent(marker, false)],
  ['multiFamilyAcquisitionProjects', (marker) => createProjectContent(marker, false)],
  ['features', null],
]);

/**
 * @param {Aggregator[]} aggregators
 * @param {any} blob
 * @return {Array<Object<string, DisplayField>, Function[]>}
 */
function runAggregators(aggregators, blob) {
  const aggregatedFields = {};
  const deleteFunctions = [];

  // Loop through the provided aggregators
  aggregators.forEach((aggregator) => {
    try {
      const [displayField, deleteFunc] = aggregator.function(blob);
      // If the delete function exists, push it to the array of delete functions
      if (deleteFunc !== undefined) {
        deleteFunctions.push(deleteFunc);
      }
      // If the display field is not undefined, add it to the aggregated fields object
      if (displayField !== undefined) {
        aggregatedFields[aggregator.name] = displayField;
      }
    } catch (error) {
      error.message = `Failed to execute data aggregator: ${aggregator?.name}\n${error.message}`;
      throw error;
    }
  });

  return [aggregatedFields, deleteFunctions];
}

function runDeleteFunctions(deleteFunctions, blob) {
  // Delete the keys that were used from the building's metadata
  deleteFunctions.forEach((deleteFunc) => {
    try {
      deleteFunc(blob);
    } catch (error) {
      console.warn(error);
      console.warn('Occurred when trying to run this delete function:', deleteFunc);
      console.warn('on this object:', blob);
    }
  });
}

/**
 * Converts a plain object to a DisplayField object.
 * @param {Object} obj - The plain object to convert
 * @return {DisplayField} - The converted DisplayField object
 */
const plainObjectToDF = (obj) => {
  obj.unit = obj.formatUnit ?? obj.unit;
  const displayField = new DisplayField(obj);

  if (obj.unit === 'link') {
    displayField.value = new Link(obj.value);
  }
  return displayField;
};

/**
 * Given an object to search and a field name, returns the display field with the given label.
 * @param {InfoWindowContent|DisplayField|Comp} object
 * @param {string} fieldName
 * @return {DisplayField|undefined}
 */
const extractField = (object, fieldName) => {
  return object.displayFields?.find((df) => df.label === fieldName);
};

/**
 * Function that can be used in JSON.stringify() on any object to prevent circular references. Replaces any circular
 * references with the string '[object Circular]'
 * @return {function(string, any): any}
 */
const getGenericCircularReplacer = () => {
  const seen = new WeakSet();
  return (k, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[object Circular]';
      seen.add(value);
    }
    return value;
  };
};

/**
 * Generates a string representation of an Address DisplayField.
 * @param {DisplayField} df
 * @return {string}
 */
const addressToString = (df) => {
  return `${extractField(df, 'Address').value ?? ''}\n${extractField(df, 'City').value ?? ''}, ${extractField(df, 'State').value ?? ''}`;
};

export {
  exportFormatFunctions,
  runAggregators,
  runDeleteFunctions,
  plainObjectToDF,
  extractField,
  getGenericCircularReplacer,
  addressToString,
};
